import { Box, Typography } from '@mui/material';
import { EmojiEvents as TrophyIcon } from '@mui/icons-material';
import type { Board } from '../../types';

interface WinnersHeroProps {
  board: Board;
}

export const WinnersHero: React.FC<WinnersHeroProps> = ({ board }) => {
  const homeColor = board.homeColor || '#509ed3';
  const awayColor = board.awayColor || '#d32f2f';
  const gameDate = new Date(board.gameDate).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Box
      sx={{
        background: `linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)`,
        borderBottom: '3px solid',
        borderImage: `linear-gradient(90deg, ${awayColor}, #FFD700, ${homeColor}) 1`,
        py: { xs: 5, md: 7 },
        px: 3,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        // Subtle noise texture overlay
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `repeating-conic-gradient(rgba(255,255,255,0.01) 0% 25%, transparent 0% 50%) 0 0 / 4px 4px`,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Trophy icon */}
      <Box
        sx={{
          display: 'inline-flex',
          mb: 2,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <TrophyIcon
          sx={{
            fontSize: { xs: '3rem', md: '4rem' },
            color: '#FFD700',
            filter: 'drop-shadow(0 0 12px rgba(255, 215, 0, 0.5))',
          }}
        />
      </Box>

      {/* Board name */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          fontSize: { xs: '1.5rem', md: '2.2rem' },
          letterSpacing: '-0.02em',
          mb: 1,
          color: '#fff',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {board.name}
      </Typography>

      {/* Team matchup */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1.5, md: 2.5 },
          mb: 1.5,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: awayColor,
            fontSize: { xs: '1.1rem', md: '1.5rem' },
            textShadow: `0 0 20px ${awayColor}66`,
          }}
        >
          {board.awayTeam}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: 'rgba(255,255,255,0.4)',
            fontSize: { xs: '0.8rem', md: '0.9rem' },
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
          }}
        >
          vs
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: homeColor,
            fontSize: { xs: '1.1rem', md: '1.5rem' },
            textShadow: `0 0 20px ${homeColor}66`,
          }}
        >
          {board.homeTeam}
        </Typography>
      </Box>

      {/* Game date */}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: 'rgba(255,255,255,0.5)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {gameDate}
      </Typography>
    </Box>
  );
};
